import { useState, useMemo, useEffect } from 'react';
import * as XLSX from 'xlsx';

// Firebase Config & Firestore SDK
import { db } from '../../../firebase/config'; 
import { collection, collectionGroup, getDocs, query, where, doc } from 'firebase/firestore';

// --- IMPORTACIÓN DE NUESTRAS UTILIDADES DE SEGUROS ---
import { 
    parseCurrency, 
    parseDateToComparable, 
    formatDateForInput 
} from '../../../utils/segurosUtils';

// --- CALCULADOR DE ESTADO DE EMISIÓN CRONOLÓGICO SEGURO PARA CONTRATOS ---
const calcularEstadoEmision = (estadoRaw, fInicioRaw, fVencRaw) => {
    let estadoCalculado = estadoRaw || 'SIN_ESTADO';
    
    if (!['ANULADA', 'CANCELADA', 'RECHAZADA', 'RESCINDIDO', 'ANULADO', 'CANCELADO'].includes(String(estadoCalculado).toUpperCase())) {
        let hoyDate = new Date();
        if (hoyDate.getFullYear() < 2026) {
            hoyDate = parseDateToComparable('2026-07-03'); // Sincronizado a fecha del sistema actual (3 de Julio de 2026)
        }

        const fInicioDate = parseDateToComparable(fInicioRaw);
        const fVencDate = parseDateToComparable(fVencRaw);

        // Validamos explícitamente que los objetos Date sean válidos antes de proceder
        if (fInicioDate && fVencDate && hoyDate) {
            hoyDate.setHours(0, 0, 0, 0);
            fInicioDate.setHours(0, 0, 0, 0);
            fVencDate.setHours(0, 0, 0, 0);

            if (hoyDate < fInicioDate) {
                estadoCalculado = 'NO INICIADO';
            } else if (hoyDate >= fInicioDate && hoyDate <= fVencDate) {
                estadoCalculado = 'VIGENTE';
            } else {
                estadoCalculado = 'VENCIDO';
            }
        }
    }
    return estadoCalculado;
};

// --- PARSER DE RUTAS UNIVERSAL PARA CONTRATOS ---
const parsePathDetails = (path) => {
    const parts = path.split('/');
    const clientesIndex = parts.indexOf('clientes');
    const contratosIndex = parts.indexOf('contratos');
    
    let clienteId = null;
    let contratoId = null;
    
    if (clientesIndex !== -1 && clientesIndex + 1 < parts.length) {
        clienteId = parts[clientesIndex + 1];
    }
    if (contratosIndex !== -1 && contratosIndex + 1 < parts.length) {
        contratoId = parts[contratosIndex + 1];
    }
    
    return { clienteId, contratoId };
};

export const useReportesContratos = () => {
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');

    const [contratos, setContratos] = useState([]);
    const [loadingProgress, setLoadingProgress] = useState({ active: false, step: 0, text: '' });
    const [isDbLoaded, setIsDbLoaded] = useState(false);
    const [queryError, setQueryError] = useState(null);

    const [activeTab, setActiveTab] = useState('TABLA'); 
    const [busquedaGlobal, setBusquedaGlobal] = useState('');
    const [debouncedBusqueda, setDebouncedBusqueda] = useState('');
    const [agruparPor, setAgruparPor] = useState('NINGUNO'); 

    // --- COLUMNAS POR DEFECTO ACTUALIZADAS ---
    const [columnasSeleccionadas, setColumnasSeleccionadas] = useState([
        'fechaCarga', 'clienteNombre', 'clienteApellido', 'clienteDni', 'clienteCuit', 
        'nContrato', 'inicioVigencia', 'finVigencia', 'tipoOperacion', 'subOperacion', 
        'estadoPoliza', 'compania', 'primaEmitida', 'canal', 'canalNombre', 
        'subcanalNombre', 'vendedor', 'suscriptor'
    ]);

    const [multiFiltros, setMultiFiltros] = useState({
        estadoPoliza: [],
        tipoOperacion: [],
        subOperacion: [],
        compania: [],
        vendedor: [],
        suscriptor: [],
        canal: [],
        canalNombre: [],
        subcanalNombre: []
    });

    // Debounce para optimizar la escritura en el campo de búsqueda global de contratos
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedBusqueda(busquedaGlobal);
        }, 300);
        return () => clearTimeout(handler);
    }, [busquedaGlobal]);

    // --- CAMPOS DISPONIBLES (Predeterminados y Opcionales sin tildar) ---
    const camposDisponibles = useMemo(() => {
        return {
            'Columnas Principales (Default)': [
                { key: 'fechaCarga', label: 'Fecha de Carga' },
                { key: 'clienteNombre', label: 'Cliente: Nombre' },
                { key: 'clienteApellido', label: 'Cliente: Apellido' },
                { key: 'clienteDni', label: 'Cliente: DNI' },
                { key: 'clienteCuit', label: 'Cliente: CUIT' },
                { key: 'nContrato', label: 'N° Contrato' },
                { key: 'inicioVigencia', label: 'Inicio Vigencia' },
                { key: 'finVigencia', label: 'Fin Vigencia' },
                { key: 'tipoOperacion', label: 'Tipo de Operación' },
                { key: 'subOperacion', label: 'Sub Operación' },
                { key: 'estadoPoliza', label: 'Estado Emisión' },
                { key: 'compania', label: 'Compañía' },
                { key: 'primaEmitida', label: 'Prima Emitida' },
                { key: 'canal', label: 'Canal' },
                { key: 'canalNombre', label: 'Nombre Canal' },
                { key: 'subcanalNombre', label: 'Subcanal / Asociado' },
                { key: 'vendedor', label: 'Vendedor' },
                { key: 'suscriptor', label: 'Suscriptor' }
            ],
            'Columnas Adicionales': [
                { key: 'ciiu', label: 'CIIU' },
                { key: 'capitas', label: 'Cápitas' },
                { key: 'moneda', label: 'Moneda' },
                { key: 'masaSalarial', label: 'Masa Salarial' },
                { key: 'alicuota', label: 'Alícuota' },
                { key: 'organizador', label: 'Organizador' },
                { key: 'pas', label: 'PAS' },
                { key: 'tipoConversion', label: 'Tipo Conversión' },
                { key: 'tieneContador', label: 'Tiene Contador' },
                { key: 'contadorNombre', label: 'Nombre Contador' },
                { key: 'porcentajeComision', label: 'Porcentaje Comisión' }
            ]
        };
    }, []);

    // Opciones únicas de filtrado para el Sidebar
    const opcionesFiltrosUnicas = useMemo(() => {
        if (!isDbLoaded) return {};
        const getUnique = (arr, field) => {
            const uniqueSet = new Set();
            arr.forEach(item => {
                const val = item[field];
                if (val) {
                    uniqueSet.add(String(val).toUpperCase().trim());
                }
            });
            return [...uniqueSet].sort();
        };
        return {
            estadoPoliza: getUnique(contratos, 'estadoPoliza'),
            tipoOperacion: getUnique(contratos, 'tipoOperacion'),
            subOperacion: getUnique(contratos, 'subOperacion'),
            compania: getUnique(contratos, 'compania'),
            vendedor: getUnique(contratos, 'vendedor'),
            suscriptor: getUnique(contratos, 'suscriptor'),
            canal: getUnique(contratos, 'canal'),
            canalNombre: getUnique(contratos, 'canalNombre'),
            subcanalNombre: getUnique(contratos, 'subcanalNombre')
        };
    }, [contratos, isDbLoaded]);

    // --- CONSULTA DINÁMICA DE CONTRATOS A FIRESTORE (Ajustado para fechaCarga) ---
    const consultarReportePorRango = async (e) => {
        if (e) e.preventDefault();
        if (!fechaDesde || !fechaHasta) return;

        setLoadingProgress({ active: true, step: 1, text: 'Identificando rango de búsqueda...' });
        setIsDbLoaded(false);
        setQueryError(null);

        try {
            setLoadingProgress({ active: true, step: 2, text: 'Buscando contratos indexados por fechaCarga...' });
            
            // AJUSTE: El motor de búsqueda de rango ahora audita estrictamente por 'fechaCarga'
            const targetQuery = query(
                collectionGroup(db, 'contratos'),
                where('fechaCarga', '>=', fechaDesde),
                where('fechaCarga', '<=', fechaHasta)
            );
            let targetSnap = await getDocs(targetQuery);

            // Fallback preventivo para Timestamps de Firestore en fechaCarga
            if (targetSnap.empty) {
                try {
                    const dateDesde = parseDateToComparable(fechaDesde);
                    const dateHasta = parseDateToComparable(fechaHasta);
                    if (dateDesde && dateHasta) {
                        dateDesde.setHours(0, 0, 0, 0); 
                        dateHasta.setHours(23, 59, 59, 999); 
                        const fallbackQuery = query(
                            collectionGroup(db, 'contratos'),
                            where('fechaCarga', '>=', dateDesde),
                            where('fechaCarga', '<=', dateHasta)
                        );
                        const fallbackSnap = await getDocs(fallbackQuery);
                        if (!fallbackSnap.empty) {
                            targetSnap = fallbackSnap;
                        }
                    }
                } catch (fallbackErr) {
                    console.warn("Falló el intento de consulta por tipo Timestamp en contratos:", fallbackErr);
                }
            }

            if (targetSnap.empty) {
                setContratos([]);
                setIsDbLoaded(true);
                setLoadingProgress({ active: false, step: 0, text: '' });
                return;
            }

            setLoadingProgress({ active: true, step: 3, text: 'Sincronizando clientes firmantes...' });
            const uniqueClientIds = [...new Set(targetSnap.docs.map(docSnap => {
                const { clienteId } = parsePathDetails(docSnap.ref.path);
                return clienteId;
            }))].filter(Boolean);

            const clientesMap = {};
            const clientChunks = [];
            for (let i = 0; i < uniqueClientIds.length; i += 30) {
                clientChunks.push(uniqueClientIds.slice(i, i + 30));
            }
            await Promise.all(clientChunks.map(async (chunk) => {
                const q = query(collection(db, 'public/data/clientes'), where('__name__', 'in', chunk));
                const snap = await getDocs(q);
                snap.docs.forEach(d => {
                    clientesMap[d.id] = { id: d.id, ...d.data() };
                });
            }));

            setLoadingProgress({ active: true, step: 4, text: 'Construyendo modelo relacional de contratos...' });
            
            const processedRows = await Promise.all(targetSnap.docs.map(async docSnap => {
                const { clienteId } = parsePathDetails(docSnap.ref.path);
                const cData = clientesMap[clienteId] || {};
                const childData = docSnap.data();

                const fInicio = childData.inicioVigencia || childData.fechaInicio;
                const fVenc = childData.finVigencia || childData.fechaVencimiento;
                
                // AJUSTE: Ejecuta de manera segura el calculador de estado cronológico y lo mapea a 'estadoPoliza'
                const estadoEmisionCalculado = calcularEstadoEmision(
                    childData.estadoPoliza || childData.estadoEmision || childData.estadoContrato || childData.estado, 
                    fInicio, 
                    fVenc
                );

                const modelProperties = {
                    clienteNombre: cData.nombre || '',
                    clienteApellido: cData.apellido || '',
                    clienteDni: cData.dni || '',
                    clienteCuit: cData.cuit || '',
                    nContrato: childData.nContrato || childData.numeroContrato || '',
                    inicioVigencia: formatDateForInput(fInicio),
                    finVigencia: formatDateForInput(fVenc),
                    fechaCarga: formatDateForInput(childData.fechaCarga || childData.fechaCargaContrato),
                    tipoOperacion: childData.tipoOperacion || childData.motivo || 'EMISION',
                    subOperacion: childData.subOperacion || '',
                    estadoPoliza: estadoEmisionCalculado,
                    compania: childData.compania || '',
                    primaEmitida: childData.primaEmitida || childData.monto || childData.premio || 0,
                    canal: childData.canal || '',
                    canalNombre: childData.canalNombre || childData.subcanal || '',
                    subcanalNombre: childData.subcanalNombre || '',
                    vendedor: childData.vendedor || 'SIN ASIGNAR',
                    suscriptor: childData.suscriptor || '',
                    
                    // Mapeo defensivo de columnas adicionales/opcionales
                    ciiu: childData.ciiu || '',
                    capitas: childData.capitas || 0,
                    moneda: childData.moneda || '$',
                    masaSalarial: childData.masaSalarial || 0,
                    alicuota: childData.alicuota || 0,
                    organizador: childData.organizador || '',
                    pas: childData.pas || '',
                    tipoConversion: childData.tipoConversion || '',
                    tieneContador: childData.tieneContador || false,
                    contadorNombre: childData.contadorNombre || '',
                    porcentajeComision: childData.porcentajeComision || 0
                };

                return {
                    id: docSnap.id,
                    path: docSnap.ref.path,
                    clienteId,
                    ...childData,
                    ...modelProperties
                };
            }));

            setContratos(processedRows);
            setIsDbLoaded(true);
            setLoadingProgress({ active: false, step: 0, text: '' });

        } catch (error) {
            console.error("Error al estructurar BI de contratos:", error);
            setQueryError(error.message || "Error al procesar la base de datos de contratos.");
            setIsDbLoaded(true);
            setLoadingProgress({ active: false, step: 0, text: '' });
        }
    };

    // --- FILTRADO SECUNDARIO OPTIMIZADO ---
    const registrosProcesados = useMemo(() => {
        if (!isDbLoaded) return [];

        return contratos.filter(row => {
            // Buscador Global (Nombre, Apellido, N° Contrato, DNI/CUIT)
            if (debouncedBusqueda) {
                const s = debouncedBusqueda.toLowerCase();
                const mNombre = `${row.clienteNombre || ''} ${row.clienteApellido || ''}`.toLowerCase().includes(s);
                const mContrato = String(row.nContrato || '').includes(s);
                const mDoc = String(row.clienteDni || row.clienteCuit || '').includes(s);
                if (!mNombre && !mContrato && !mDoc) return false;
            }

            // Multi-filtros del Sidebar actualizados
            const matchMulti = (campoKey, recordKey) => {
                const seleccionados = multiFiltros[campoKey] || [];
                if (seleccionados.length === 0) return true;
                const normalizedRecordVal = String(row[recordKey] || '').toUpperCase().trim();
                return seleccionados.includes(normalizedRecordVal);
            };

            if (!matchMulti('estadoPoliza', 'estadoPoliza') ||
                !matchMulti('tipoOperacion', 'tipoOperacion') ||
                !matchMulti('subOperacion', 'subOperacion') ||
                !matchMulti('compania', 'compania') ||
                !matchMulti('vendedor', 'vendedor') ||
                !matchMulti('suscriptor', 'suscriptor') ||
                !matchMulti('canal', 'canal') ||
                !matchMulti('canalNombre', 'canalNombre') ||
                !matchMulti('subcanalNombre', 'subcanalNombre')
            ) {
                return false;
            }

            return true;
        });
    }, [contratos, multiFiltros, debouncedBusqueda, isDbLoaded]);

    // --- AGRUPAMIENTO EN MEMORIA ---
    const registrosAgrupados = useMemo(() => {
        if (agruparPor === 'NINGUNO') return null;
        const grupos = {};
        registrosProcesados.forEach(row => {
            const key = row[agruparPor] || 'SIN ESPECIFICAR';
            if (!grupos[key]) grupos[key] = { nombre: key, items: [], totalMonto: 0 };
            grupos[key].items.push(row);
            if (row.primaEmitida) grupos[key].totalMonto += Number(row.primaEmitida) || 0;
        });
        return Object.values(grupos).sort((a, b) => b.items.length - a.items.length);
    }, [registrosProcesados, agruparPor]);

    // --- EXPORTACIÓN DE CONTRATOS A EXCEL ---
    const exportarExcel = () => {
        if (registrosProcesados.length === 0) return;
        
        const camposMapeados = [];
        Object.values(camposDisponibles).forEach(seccion => {
            camposMapeados.push(...seccion);
        });

        const datosExcel = registrosProcesados.map(row => {
            const celda = {};
            columnasSeleccionadas.forEach(k => {
                const def = camposMapeados.find(c => c.key === k);
                const label = def ? def.label : k;
                let val = row[k];
                if (k === 'primaEmitida' || k === 'masaSalarial') val = parseCurrency(val);
                if (k === 'tieneContador') val = val ? 'SÍ' : 'NO';
                celda[label] = val !== undefined ? val : '';
            });
            return celda;
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(datosExcel);
        ws['!cols'] = columnasSeleccionadas.map(() => ({ wch: 22 }));
        XLSX.utils.book_append_sheet(wb, ws, "Contratos");

        if (agruparPor !== 'NINGUNO' && registrosAgrupados) {
            const resumen = registrosAgrupados.map(g => ({ 
                'Grupo': g.nombre, 
                'Cantidad Contratos': g.items.length, 
                'Monto Acumulado (Primas)': g.totalMonto 
            }));
            const wsR = XLSX.utils.json_to_sheet(resumen);
            XLSX.utils.book_append_sheet(wb, wsR, "Resumen Agrupado");
        }
        XLSX.writeFile(wb, `Reporte_BI_Contratos_${fechaDesde}_a_${fechaHasta}.xlsx`);
    };

    return {
        fechaDesde, setFechaDesde,
        fechaHasta, setFechaHasta,
        loadingProgress,
        isDbLoaded,
        queryError,
        activeTab, setActiveTab,
        columnasSeleccionadas, setColumnasSeleccionadas,
        busquedaGlobal, setBusquedaGlobal,
        agruparPor, setAgruparPor,
        multiFiltros, setMultiFiltros,
        camposDisponibles,
        opcionesFiltrosUnicas,
        registrosProcesados,
        registrosAgrupados,
        consultarReportePorRango,
        exportarExcel
    };
};